import type {
  UpdatableCategory,
  AllCategory,
  HealthUpdate,
  ArchivedHealthUpdate,
  HealthUpdateListResponse,
  HealthUpdateAuditEntry,
} from '../types/healthUpdate';
import { CATEGORY_IDENTITY_FIELDS } from '../types/healthUpdate';

const S3_FILENAME = 'health-updates.json';
const AUDIT_FILENAME = 'health-updates-audit.json';
const ARCHIVE_FILENAME = 'health-updates-archive.json';

// Map categories to their event-date field keys (most relevant date first).
// Categories not listed here have no event date and sort by submittedAt.
const EVENT_DATE_FIELDS: Partial<Record<AllCategory, string[]>> = {
  conditions: ['dateOfOnset'],
  medications: ['dateLastDispensed', 'dateFirstDispensed'],
  vaccineHistory: ['date'],
  surgicalHistory: ['date'],
  travelHistory: ['startDate'],
  careTeam: ['dateOfLastVisit'],
  diagnosticHistory: ['date'],
};

/**
 * Sort updates so the most recent appear first.
 * - For categories with an event-date field, sort by that date descending.
 * - Otherwise sort by submittedAt descending (most recent entry first).
 * If an event-date value can't be parsed, falls back to submittedAt.
 */
function sortUpdates(updates: HealthUpdate[]): HealthUpdate[] {
  return [...updates].sort((a, b) => {
    return getSortDate(b) - getSortDate(a);
  });
}

function getSortDate(update: HealthUpdate): number {
  const dateFields = EVENT_DATE_FIELDS[update.category];
  if (dateFields) {
    for (const field of dateFields) {
      const value = update.data[field];
      if (value) {
        const parsed = Date.parse(value);
        if (!isNaN(parsed)) return parsed;
      }
    }
  }
  // Fallback: use submission timestamp
  return Date.parse(update.submittedAt) || 0;
}

function generateUpdateId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

async function getUsername(): Promise<string> {
  const { fetchAuthSession } = await import('aws-amplify/auth');
  const session = await fetchAuthSession();
  const username = session.tokens?.idToken?.payload?.['cognito:username'] as string;
  if (!username) throw new Error('Not authenticated');
  return username;
}

async function readUpdatesFromS3(s3Path: string): Promise<HealthUpdate[]> {
  const { getUrl } = await import('aws-amplify/storage');
  try {
    const result = await getUrl({ path: s3Path });
    const response = await fetch(result.url.toString());
    if (!response.ok) throw new Error('Not found');
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch {
    // File doesn't exist yet — start with empty array
    return [];
  }
}

async function writeUpdatesToS3(s3Path: string, updates: HealthUpdate[]): Promise<void> {
  const { uploadData } = await import('aws-amplify/storage');
  const blob = new Blob([JSON.stringify(updates, null, 2)], { type: 'application/json' });
  await uploadData({
    path: s3Path,
    data: blob,
    options: { contentType: 'application/json' },
  }).result;
}

async function readAuditFromS3(s3Path: string): Promise<HealthUpdateAuditEntry[]> {
  const { getUrl } = await import('aws-amplify/storage');
  try {
    const result = await getUrl({ path: s3Path });
    const response = await fetch(result.url.toString());
    if (!response.ok) throw new Error('Not found');
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

async function appendAuditEntry(s3Path: string, entry: HealthUpdateAuditEntry): Promise<void> {
  const { uploadData } = await import('aws-amplify/storage');
  const existing = await readAuditFromS3(s3Path);
  existing.push(entry);
  const blob = new Blob([JSON.stringify(existing, null, 2)], { type: 'application/json' });
  await uploadData({
    path: s3Path,
    data: blob,
    options: { contentType: 'application/json' },
  }).result;
}

async function readArchiveFromS3(s3Path: string): Promise<ArchivedHealthUpdate[]> {
  const { getUrl } = await import('aws-amplify/storage');
  try {
    const result = await getUrl({ path: s3Path });
    const response = await fetch(result.url.toString());
    if (!response.ok) throw new Error('Not found');
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

async function writeArchiveToS3(s3Path: string, entries: ArchivedHealthUpdate[]): Promise<void> {
  const { uploadData } = await import('aws-amplify/storage');
  const blob = new Blob([JSON.stringify(entries, null, 2)], { type: 'application/json' });
  await uploadData({
    path: s3Path,
    data: blob,
    options: { contentType: 'application/json' },
  }).result;
}

function findMatchingEntry(
  entries: HealthUpdate[],
  category: AllCategory,
  data: Record<string, string>
): HealthUpdate | null {
  const identityFields = CATEGORY_IDENTITY_FIELDS[category];
  if (!identityFields || identityFields.length === 0) return null;

  return entries.find(entry => {
    if (entry.category !== category) return false;
    return identityFields.every(field => {
      const existing = (entry.data[field] || '').trim().toLowerCase();
      const incoming = (data[field] || '').trim().toLowerCase();
      return existing === incoming && existing.length > 0;
    });
  }) || null;
}

async function archiveMatchingEntry(
  username: string,
  category: AllCategory,
  newUpdateId: string,
  data: Record<string, string>,
  performedBy: string
): Promise<HealthUpdate[]> {
  const s3Path = `users/${username}/health-updates/${S3_FILENAME}`;
  const entries = await readUpdatesFromS3(s3Path);
  const match = findMatchingEntry(entries, category, data);

  if (!match) return entries;

  // Archive the matched entry
  const archivePath = `users/${username}/health-updates/${ARCHIVE_FILENAME}`;
  const archive = await readArchiveFromS3(archivePath);
  const archivedEntry: ArchivedHealthUpdate = {
    ...match,
    archivedAt: new Date().toISOString(),
    replacedBy: newUpdateId,
  };
  archive.push(archivedEntry);
  await writeArchiveToS3(archivePath, archive);

  // Remove matched entry from active array
  const filtered = entries.filter(e => e.updateId !== match.updateId);

  // Append audit entry for archival
  const auditPath = `users/${username}/health-updates/${AUDIT_FILENAME}`;
  await appendAuditEntry(auditPath, {
    timestamp: new Date().toISOString(),
    action: 'archived',
    performedBy,
    updateId: match.updateId,
    category: match.category,
    data: match.data,
  });

  return filtered;
}

// --- Patient endpoints ---

export async function submitHealthUpdate(
  category: UpdatableCategory,
  data: Record<string, string>,
  acknowledgement: { confirmed: boolean; signedName: string }
): Promise<{ updateId: string; submittedAt: string }> {
  const username = await getUsername();
  const s3Path = `users/${username}/health-updates/${S3_FILENAME}`;

  const updateId = generateUpdateId();
  const submittedAt = new Date().toISOString();

  const newEntry: HealthUpdate = {
    updateId,
    category,
    data,
    username,
    submittedAt,
    acknowledgement: {
      confirmed: acknowledgement.confirmed,
      signedName: acknowledgement.signedName,
      signedAt: submittedAt,
    },
  };

  // Archive any matching entry before adding the new one
  const existing = await archiveMatchingEntry(username, category, updateId, data, acknowledgement.signedName);
  existing.push(newEntry);
  await writeUpdatesToS3(s3Path, existing);

  const auditPath = `users/${username}/health-updates/${AUDIT_FILENAME}`;
  await appendAuditEntry(auditPath, {
    timestamp: submittedAt,
    action: 'added',
    performedBy: acknowledgement.signedName,
    updateId,
    category,
    data,
  });

  return { updateId, submittedAt };
}

export async function listHealthUpdates(category?: UpdatableCategory): Promise<HealthUpdateListResponse> {
  const username = await getUsername();
  const s3Path = `users/${username}/health-updates/${S3_FILENAME}`;

  let updates = await readUpdatesFromS3(s3Path);
  if (category) {
    updates = updates.filter(u => u.category === category);
  }
  return { updates: sortUpdates(updates) };
}

// --- Admin endpoints ---

export async function adminListHealthUpdates(
  username: string,
  category?: AllCategory
): Promise<HealthUpdateListResponse> {
  const s3Path = `users/${username}/health-updates/${S3_FILENAME}`;

  let updates = await readUpdatesFromS3(s3Path);
  if (category) {
    updates = updates.filter(u => u.category === category);
  }
  return { updates: sortUpdates(updates) };
}

export async function adminSubmitHealthUpdate(
  username: string,
  category: AllCategory,
  data: Record<string, string>,
  adminName: string
): Promise<{ updateId: string; submittedAt: string }> {
  const s3Path = `users/${username}/health-updates/${S3_FILENAME}`;

  const updateId = generateUpdateId();
  const submittedAt = new Date().toISOString();

  const newEntry: HealthUpdate = {
    updateId,
    category,
    data,
    username,
    submittedAt,
    acknowledgement: {
      confirmed: true,
      signedName: `Admin: ${adminName}`,
      signedAt: submittedAt,
    },
  };

  // Archive any matching entry before adding the new one
  const existing = await archiveMatchingEntry(username, category, updateId, data, `Admin: ${adminName}`);
  existing.push(newEntry);
  await writeUpdatesToS3(s3Path, existing);

  const auditPath = `users/${username}/health-updates/${AUDIT_FILENAME}`;
  await appendAuditEntry(auditPath, {
    timestamp: submittedAt,
    action: 'added',
    performedBy: `Admin: ${adminName}`,
    updateId,
    category,
    data,
  });

  return { updateId, submittedAt };
}

export async function adminUpdateHealthUpdate(
  username: string,
  updateId: string,
  data: Record<string, string>
): Promise<void> {
  const s3Path = `users/${username}/health-updates/${S3_FILENAME}`;
  const updates = await readUpdatesFromS3(s3Path);
  const index = updates.findIndex(u => u.updateId === updateId);
  if (index === -1) throw new Error('Update not found');
  const previousData = { ...updates[index].data };
  const category = updates[index].category;
  updates[index].data = data;
  await writeUpdatesToS3(s3Path, updates);

  const adminUsername = await getUsername();
  const auditPath = `users/${username}/health-updates/${AUDIT_FILENAME}`;
  await appendAuditEntry(auditPath, {
    timestamp: new Date().toISOString(),
    action: 'edited',
    performedBy: `Admin: ${adminUsername}`,
    updateId,
    category,
    data,
    previousData,
  });
}

export async function adminDeleteHealthUpdate(
  username: string,
  updateId: string
): Promise<void> {
  const s3Path = `users/${username}/health-updates/${S3_FILENAME}`;
  const updates = await readUpdatesFromS3(s3Path);
  const deleted = updates.find(u => u.updateId === updateId);
  if (!deleted) throw new Error('Update not found');
  const filtered = updates.filter(u => u.updateId !== updateId);
  await writeUpdatesToS3(s3Path, filtered);

  const adminUsername = await getUsername();
  const auditPath = `users/${username}/health-updates/${AUDIT_FILENAME}`;
  await appendAuditEntry(auditPath, {
    timestamp: new Date().toISOString(),
    action: 'deleted',
    performedBy: `Admin: ${adminUsername}`,
    updateId,
    category: deleted.category,
    data: deleted.data,
  });
}

// --- Archive endpoint ---

export async function adminGetArchivedUpdates(
  username: string,
  category?: AllCategory
): Promise<ArchivedHealthUpdate[]> {
  const archivePath = `users/${username}/health-updates/${ARCHIVE_FILENAME}`;
  let entries = await readArchiveFromS3(archivePath);
  if (category) {
    entries = entries.filter(e => e.category === category);
  }
  return entries.sort((a, b) =>
    new Date(b.archivedAt).getTime() - new Date(a.archivedAt).getTime()
  );
}

// --- Audit log endpoint ---

export async function adminGetUpdateAuditLog(
  username: string
): Promise<HealthUpdateAuditEntry[]> {
  const auditPath = `users/${username}/health-updates/${AUDIT_FILENAME}`;
  const entries = await readAuditFromS3(auditPath);
  return entries.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}
